import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, SendHorizonal, Check, Mic, Volume2, VolumeX, AudioLines, User, LogOut } from "lucide-react";
import { getSessionId } from "@/utils/session";

const VISITOR_TOKEN_KEY = "alkyrvis_visitor_token";

const getVisitorToken = () => {
  try {
    return localStorage.getItem(VISITOR_TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What can Alkyrvis automate?",
  "Can it answer my phone calls?",
  "How does the AI workforce work?",
];

const WELCOME =
  "Hi, I'm the Alkyrvis Concierge — tell me what you're trying to automate, and I'll tell you which AI employee handles it.";

const getWelcome = () => {
  try {
    const name = localStorage.getItem("alkyrvis_name");
    const first = name ? name.split(" ")[0] : null;
    if (first) {
      return `Welcome back, ${first}. What are we automating today?`;
    }
  } catch {
    /* no stored name */
  }
  return "Hi, I'm the Alkyrvis Concierge — tell me what you're trying to automate, and I'll tell you which AI employee handles it.";
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: getWelcome() },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const sessionRef = useRef(getSessionId());
  const scrollRef = useRef(null);
  const autoOpened = useRef(false);
  const sendRef = useRef(null);
  const [user, setUser] = useState(null);
  const [teaser, setTeaser] = useState(false);
  const [view, setView] = useState("chat");
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [authBusy, setAuthBusy] = useState(false);

  useEffect(() => {
    const t = getVisitorToken();
    if (!t) return;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (!u) return;
        setUser(u);
        try {
          localStorage.setItem("alkyrvis_name", u.name);
        } catch {
          /* storage unavailable */
        }
        setMessages((m) =>
          m.length === 1
            ? [
                {
                  role: "assistant",
                  content: `Welcome back, ${u.name.split(" ")[0]}. What are we automating today?`,
                },
              ]
            : m
        );
      })
      .catch(() => {});
  }, []);

  const submitAuth = async () => {
    if (authBusy) return;
    setAuthError("");
    setAuthBusy(true);
    try {
      const path = authMode === "login" ? "login" : "register";
      const body =
        authMode === "login"
          ? { email: authForm.email, password: authForm.password }
          : { name: authForm.name, email: authForm.email, password: authForm.password };
      const res = await fetch(`${API}/auth/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok) {
        const d = j.detail;
        setAuthError(
          typeof d === "string"
            ? d
            : Array.isArray(d)
              ? d.map((e) => e.msg || "").join(" ")
              : "Something went wrong."
        );
        setAuthBusy(false);
        return;
      }
      try {
        localStorage.setItem(VISITOR_TOKEN_KEY, j.token);
        localStorage.setItem("alkyrvis_name", j.name);
      } catch {
        /* storage unavailable */
      }
      const u = { name: j.name, email: j.email };
      setUser(u);
      window.dispatchEvent(new CustomEvent("visitor-auth-changed"));
      setView("chat");
      setAuthForm({ name: "", email: "", password: "" });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            authMode === "register"
              ? `Lovely to meet you, ${j.name.split(" ")[0]}. You're signed in — I'll recognise you on any device from now on.`
              : `Welcome back, ${j.name.split(" ")[0]}. Good to see you again.`,
        },
      ]);
    } catch {
      setAuthError("Connection failed. Please try again.");
    }
    setAuthBusy(false);
  };

  const signOut = () => {
    try {
      localStorage.removeItem(VISITOR_TOKEN_KEY);
    } catch {
      /* storage unavailable */
    }
    setUser(null);
    window.dispatchEvent(new CustomEvent("visitor-auth-changed"));
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // welcome teaser bubble — shows briefly, never auto-opens the chat
  useEffect(() => {
    if (open || teaser) return undefined;
    const show = setTimeout(() => setTeaser(true), 6000);
    return () => clearTimeout(show);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!teaser) return undefined;
    const hide = setTimeout(() => setTeaser(false), 30000);
    return () => clearTimeout(hide);
  }, [teaser]);

  useEffect(() => {
    if (open) setTeaser(false);
  }, [open]);

  useEffect(() => {
    const handler = () => {
      autoOpened.current = true;
      setOpen(true);
      if (!user) setView("auth");
    };
    window.addEventListener("open-signin", handler);
    return () => window.removeEventListener("open-signin", handler);
  }, [user]);

  useEffect(() => {
    const handler = (e) => {
      autoOpened.current = true;
      setOpen(true);
      const msg = e.detail && e.detail.message;
      if (msg && sendRef.current) {
        setTimeout(() => sendRef.current(msg), 500);
      }
    };
    window.addEventListener("open-concierge", handler);
    return () => window.removeEventListener("open-concierge", handler);
  }, []);

  const speak = async (text) => {
    try {
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.slice(0, 1000) }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play();
    } catch {
      /* voice unavailable */
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        try {
          const res = await fetch(`${API}/stt`, { method: "POST", body: fd });
          const j = await res.json();
          if (j.text) send(j.text);
        } catch {
          /* transcription failed */
        }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      /* mic unavailable */
    }
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    let replyText = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(getVisitorToken() ? { Authorization: `Bearer ${getVisitorToken()}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const j = JSON.parse(data);
            if (j.lead_saved) {
              if (j.name) {
                try {
                  localStorage.setItem("alkyrvis_name", j.name);
                } catch {
                  /* storage unavailable */
                }
              }
              setMessages((m) => [
                ...m,
                {
                  role: "system",
                  content:
                    "Details saved — the Alkyrvis team will be in touch within 24 hours.",
                },
              ]);
              continue;
            }
            const chunk = j.delta || j.error || "";
            if (chunk) {
              replyText += chunk;
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: last.content + chunk };
                return copy;
              });
            }
          } catch {
            /* partial chunk */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Connection interrupted. Please try again.",
        };
        return copy;
      });
    }
    setStreaming(false);
    if (voiceOn && replyText.trim()) speak(replyText.trim());
  };
  sendRef.current = send;

  return (
    <>
      <AnimatePresence>
        {teaser && !open && (
          <motion.button
            type="button"
            data-testid="chat-teaser"
            onClick={() => {
              setTeaser(false);
              setOpen(true);
            }}
            initial={{ opacity: 0, y: 14, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.92 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="glass-panel fixed bottom-24 right-6 z-[60] w-64 rounded-2xl rounded-br-sm border-gold/25 bg-[#0A0A0A]/95 p-4 text-left shadow-[0_20px_50px_rgba(0,0,0,0.7)] transition-[border-color] duration-300 hover:border-gold/50"
          >
            <p className="text-xs leading-relaxed text-[#E8E8E8]">
              Hi, I'm the Alkyrvis Concierge — tell me what you're trying to
              automate.
            </p>
            <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-gold">
              Tap to talk
            </p>
          </motion.button>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        data-testid="chat-widget-button"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.6, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-gold text-[#050505] shadow-[0_0_35px_rgba(216,192,138,0.45)] transition-[box-shadow] duration-300 hover:shadow-[0_0_55px_rgba(216,192,138,0.65)]"
        aria-label="Open Alkyrvis AI chat"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        {!open && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-60" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-[#050505] bg-mint" />
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, x: 90 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 90 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="glass-panel fixed bottom-24 right-6 z-[60] flex h-[540px] max-h-[calc(100vh-8rem)] w-[390px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border-gold/20 bg-[#0A0A0A]/90 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8),0_0_60px_rgba(216,192,138,0.12)]"
          >
            <div className="flex items-center justify-between border-b border-white/5 px-5 py-4">
              <div className="flex items-center gap-3">
                <img
                  src="/assets/emblem.png"
                  alt=""
                  className="h-8 w-8 object-contain drop-shadow-[0_0_8px_rgba(216,192,138,0.5)]"
                />
                <div>
                  <p className="font-display text-xs font-medium uppercase tracking-[0.25em] text-[#F5F5F5]">
                    Alkyrvis Concierge
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-mint">
                    <span className="status-dot" style={{ width: 5, height: 5 }} />
                    Live AI — Online now
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {user ? (
                  <button
                    type="button"
                    data-testid="chat-user-chip"
                    onClick={signOut}
                    title={`Signed in as ${user.name} — tap to sign out`}
                    className="flex h-6 w-6 items-center justify-center rounded-full border border-gold/40 bg-gold/15 font-mono text-[10px] font-medium text-gold transition-[background-color] duration-300 hover:bg-gold/25"
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="chat-signin-button"
                    onClick={() => setView(view === "auth" ? "chat" : "auth")}
                    className="text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
                    aria-label="Sign in"
                    title="Sign in — be recognised on any device"
                  >
                    <User className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  data-testid="chat-voice-mode-button"
                  onClick={() => window.dispatchEvent(new CustomEvent("open-voice-orb"))}
                  className="text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
                  aria-label="Open voice mode"
                  title="Voice mode"
                >
                  <AudioLines className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  data-testid="chat-voice-toggle"
                  onClick={() => setVoiceOn((v) => !v)}
                  className={`transition-colors duration-300 ${
                    voiceOn ? "text-gold" : "text-[#A3A3A3] hover:text-gold"
                  }`}
                  aria-label={voiceOn ? "Mute concierge voice" : "Enable concierge voice"}
                  title={voiceOn ? "Voice replies on" : "Voice replies off"}
                >
                  {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  data-testid="chat-close-button"
                  onClick={() => setOpen(false)}
                  className="text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
                  aria-label="Close chat"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {view === "auth" ? (
              <div
                className="flex flex-1 flex-col justify-center overflow-y-auto px-7 py-6"
                data-testid="chat-auth-view"
              >
                <p className="font-display text-lg font-light tracking-tight text-[#F5F5F5]">
                  {authMode === "login" ? "Welcome back." : "Create your account."}
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-[#A3A3A3]">
                  {authMode === "login"
                    ? "Sign in and I'll recognise you on any device."
                    : "One account — I'll remember you everywhere, on every device."}
                </p>
                <div className="mt-6 space-y-3">
                  {authMode === "register" && (
                    <input
                      value={authForm.name}
                      onChange={(e) => setAuthForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                      data-testid="auth-name-input"
                      className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-[#F5F5F5] outline-none transition-[border-color] duration-300 placeholder:text-white/25 focus:border-gold/50"
                    />
                  )}
                  <input
                    value={authForm.email}
                    onChange={(e) => setAuthForm((f) => ({ ...f, email: e.target.value }))}
                    type="email"
                    placeholder="Email"
                    data-testid="auth-email-input"
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-[#F5F5F5] outline-none transition-[border-color] duration-300 placeholder:text-white/25 focus:border-gold/50"
                  />
                  <input
                    value={authForm.password}
                    onChange={(e) => setAuthForm((f) => ({ ...f, password: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && submitAuth()}
                    type="password"
                    placeholder={authMode === "login" ? "Password" : "Password (6+ characters)"}
                    data-testid="auth-password-input"
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-[#F5F5F5] outline-none transition-[border-color] duration-300 placeholder:text-white/25 focus:border-gold/50"
                  />
                </div>
                {authError && (
                  <p data-testid="auth-error" className="mt-3 font-mono text-[11px] text-red-400">
                    {authError}
                  </p>
                )}
                <button
                  type="button"
                  data-testid="auth-submit-button"
                  onClick={submitAuth}
                  disabled={
                    authBusy ||
                    !authForm.email.trim() ||
                    !authForm.password ||
                    (authMode === "register" && !authForm.name.trim())
                  }
                  className="mt-5 w-full rounded-full bg-gold py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[#050505] transition-[background-color,box-shadow] duration-300 hover:bg-gold-bright hover:shadow-[0_0_35px_rgba(216,192,138,0.4)] disabled:opacity-40"
                >
                  {authBusy ? "One moment..." : authMode === "login" ? "Sign In" : "Create Account"}
                </button>
                <button
                  type="button"
                  data-testid="auth-switch-mode"
                  onClick={() => {
                    setAuthMode(authMode === "login" ? "register" : "login");
                    setAuthError("");
                  }}
                  className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
                >
                  {authMode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
                </button>
              </div>
            ) : (
              <>
            <div
              ref={scrollRef}
              data-testid="chat-message-list"
              className="flex-1 space-y-4 overflow-y-auto px-5 py-5"
            >
              {messages.map((m, i) =>
                m.role === "system" ? (
                  <div
                    key={i}
                    className="flex justify-center"
                    data-testid={`chat-message-system-${i}`}
                  >
                    <span className="flex items-center gap-2 rounded-full border border-mint/30 bg-mint/[0.08] px-4 py-1.5 font-mono text-[10px] tracking-wide text-mint">
                      <Check className="h-3 w-3" />
                      {m.content}
                    </span>
                  </div>
                ) : (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-message-${m.role}-${i}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-gold/15 text-gold-bright border border-gold/25"
                        : "rounded-bl-sm border border-white/8 bg-white/[0.04] text-[#E8E8E8]"
                    }`}
                  >
                    {m.content ||
                      (streaming && i === messages.length - 1 ? (
                        <span className="flex gap-1.5 py-1">
                          {[0, 1, 2].map((d) => (
                            <motion.span
                              key={d}
                              animate={{ opacity: [0.25, 1, 0.25] }}
                              transition={{ duration: 1, repeat: Infinity, delay: d * 0.18 }}
                              className="h-1.5 w-1.5 rounded-full bg-gold"
                            />
                          ))}
                        </span>
                      ) : (
                        ""
                      ))}
                  </div>
                </div>
              ))}
            </div>

            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 px-5 pb-3">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`chat-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => send(s)}
                    className="rounded-full border border-gold/25 bg-gold/5 px-3.5 py-1.5 font-mono text-[10px] tracking-wide text-gold transition-[background-color,border-color] duration-300 hover:border-gold/50 hover:bg-gold/15"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-white/5 p-4">
              <div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-black/50 px-4 py-1.5 transition-[border-color] duration-300 focus-within:border-gold/50">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about your AI workforce..."
                  data-testid="chat-input"
                  className="w-full bg-transparent py-2 text-sm text-[#F5F5F5] outline-none placeholder:text-white/25"
                />
                <button
                  type="button"
                  data-testid="chat-mic-button"
                  onClick={toggleRecording}
                  className={`transition-[color,transform] duration-300 hover:scale-110 ${
                    recording ? "animate-pulse text-red-400" : "text-[#A3A3A3] hover:text-gold"
                  }`}
                  aria-label={recording ? "Stop recording" : "Speak to the concierge"}
                  title={recording ? "Listening... tap to stop" : "Speak to the concierge"}
                >
                  <Mic className="h-4.5 w-4.5" />
                </button>
                <button
                  type="button"
                  data-testid="chat-send-button"
                  onClick={() => send()}
                  disabled={streaming || !input.trim()}
                  className="text-gold transition-[color,transform] duration-300 hover:scale-110 hover:text-gold-bright disabled:opacity-30"
                  aria-label="Send message"
                >
                  <SendHorizonal className="h-4.5 w-4.5" />
                </button>
              </div>
              <p className="mt-2.5 text-center font-mono text-[9px] uppercase tracking-[0.25em] text-white/25">
                Powered by Alkyrvis AI
              </p>
            </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

